import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, TIMEOUTS } from "@/lib/api";
import { asculta, rosteste } from "@/lib/voiceLoop";
import { Mic, MicOff, Loader2, Volume2, AlertTriangle } from "lucide-react";

/**
 * Conversație vorbită, fără mâini.
 *
 * Componenta NU știe ce e cuvântul de trezire, când se termină o replică, sau
 * ce se rostește dintr-un răspuns. Toate vin de la server; aici se pornește
 * microfonul, se trimite sunetul, se rostește ce spune serverul să se
 * rostească, și se raportează înapoi ce s-a întâmplat.
 */
export default function VoicePanel({ workspaceId, channelId }) {
  const [config, setConfig] = useState(null);
  const [profil, setProfil] = useState(null);
  const [sesiune, setSesiune] = useState(null);
  const [stare, setStare] = useState("idle");
  const [eticheta, setEticheta] = useState("oprită");
  const [nivel, setNivel] = useState(0);
  const [istoric, setIstoric] = useState([]);
  const [eroare, setEroare] = useState("");
  const [ocupat, setOcupat] = useState(false);

  const streamRef = useRef(null);
  const ruleazaRef = useRef(false);
  const sesiuneRef = useRef(null);

  useEffect(() => {
    if (!workspaceId) return;
    api.get("/voice/profiles", { params: { workspace_id: workspaceId } })
      .then((r) => {
        setConfig(r.data);
        setProfil((p) => p || r.data.implicit);
      })
      .catch((e) => setEroare(e?.response?.data?.detail || "Nu am putut citi profilele de voce"));
  }, [workspaceId]);

  const eveniment = useCallback(async (ev) => {
    if (!sesiuneRef.current) return null;
    try {
      const { data } = await api.post("/voice/event", {
        workspace_id: workspaceId, sesiune: sesiuneRef.current, eveniment: ev,
      });
      setStare(data.stare);
      setEticheta(data.eticheta);
      return data;
    } catch {
      return null;
    }
  }, [workspaceId]);

  const opreste = useCallback(async () => {
    ruleazaRef.current = false;
    if (typeof window.speechSynthesis !== "undefined") window.speechSynthesis.cancel();
    await eveniment("stop");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    sesiuneRef.current = null;
    setSesiune(null);
    setStare("idle");
    setEticheta("oprită");
    setNivel(0);
  }, [eveniment]);

  const bucla = useCallback(async (cfgProfil) => {
    while (ruleazaRef.current) {
      let sunet;
      try {
        setStare("listening");
        setEticheta(config?.stari?.listening || "ascult");
        sunet = await asculta(streamRef.current, {
          tacereMs: config?.tacere_ms ?? 1200,
          maxSecunde: config?.max_replica_s ?? 30,
          onNivel: setNivel,
        });
      } catch (e) {
        setEroare(`Microfonul nu a putut fi citit: ${e.message}`);
        ruleazaRef.current = false;
        break;
      }
      if (!ruleazaRef.current) break;
      // Tăcere completă: nimic de trimis. Nu costă nici transcriere, nici model.
      if (!sunet) continue;

      const form = new FormData();
      form.append("file", sunet, "replica.webm");
      let r;
      try {
        setStare("thinking");
        setEticheta(config?.stari?.thinking || "mă gândesc");
        const resp = await api.post("/voice/turn", form, {
          params: { workspace_id: workspaceId, sesiune: sesiuneRef.current },
          headers: { "Content-Type": "multipart/form-data" },
          timeout: TIMEOUTS.voiceTurn,
        });
        r = resp.data;
      } catch (e) {
        setEroare(e?.response?.data?.detail || "Replica nu a ajuns la server");
        setStare("listening");
        continue;
      }

      setStare(r.stare);
      setEticheta(r.eticheta || r.stare);
      if (r.transcript) {
        setIstoric((h) => [...h, {
          cine: "eu", text: r.transcript, pentru_mine: r.pentru_mine,
          nota: r.nota_replica,
        }]);
      }
      // Nu mie mi s-a vorbit: bucla rămâne unde e, fără să rostească nimic.
      if (!r.pentru_mine || !r.rostit) continue;

      setIstoric((h) => [...h, {
        cine: "ea", text: r.raspuns, rostit: r.rostit,
        mai_e_scris: r.mai_e_scris, model: r.model, unealta: r.unealta,
      }]);
      const cum = await rosteste(r.rostit, streamRef.current, {
        barge_in: cfgProfil?.barge_in !== false,
        onIntrerupt: () => eveniment("interrupt"),
      });
      if (cum !== "intrerupt") await eveniment("spoke");
    }
  }, [config, workspaceId, eveniment]);

  const porneste = useCallback(async () => {
    setEroare("");
    setOcupat(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { data } = await api.post("/voice/session", {
        workspace_id: workspaceId, profil, channel_id: channelId || null,
      });
      sesiuneRef.current = data.sesiune;
      setSesiune(data.sesiune);
      setStare(data.stare);
      setEticheta(data.eticheta);
      ruleazaRef.current = true;
      const cfgProfil = (config?.profile || []).find((p) => p.cheie === data.profil);
      bucla(cfgProfil?.efectiv || cfgProfil);
    } catch (e) {
      setEroare(e?.response?.data?.detail || e.message || "Nu am putut porni bucla");
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    } finally {
      setOcupat(false);
    }
  }, [workspaceId, profil, channelId, config, bucla]);

  useEffect(() => () => {
    ruleazaRef.current = false;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (typeof window.speechSynthesis !== "undefined") window.speechSynthesis.cancel();
  }, []);

  const activ = Boolean(sesiune);
  const profilAles = (config?.profile || []).find((p) => p.cheie === profil);

  return (
    <section className="bg-surface border border-line/10" data-testid="voice-panel">
      <div className="px-5 py-4 border-b border-line/10 flex items-center gap-2">
        <Volume2 className="w-4 h-4 text-brand" />
        <h3 className="font-bold tracking-tight">Vorbește cu Atena</h3>
        <span className="label-tiny ml-2" data-testid="voice-stare">// {eticheta}</span>
        {activ && (
          <span className="ml-auto h-2 w-24 bg-surface-2 overflow-hidden" aria-hidden>
            <span className="block h-full bg-brand transition-[width] duration-100"
                  style={{ width: `${Math.min(100, Math.round(nivel * 900))}%` }} />
          </span>
        )}
      </div>

      <div className="p-5 space-y-4">
        {config && !config.transcriere_locala && (
          <p className="text-xs text-amber-400 flex items-start gap-2" data-testid="voice-fara-whisper">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            Transcrierea locală nu e disponibilă pe server, deci bucla nu poate auzi nimic.
          </p>
        )}
        {eroare && <p className="text-xs text-red-400" data-testid="voice-eroare">{eroare}</p>}

        <div className="flex items-center gap-3">
          <select
            data-testid="voice-profil" className="input-flat text-sm" value={profil || ""}
            disabled={activ} onChange={(e) => setProfil(e.target.value)}
          >
            {(config?.profile || []).map((p) => (
              <option key={p.cheie} value={p.cheie}>{p.efectiv?.eticheta || p.eticheta}</option>
            ))}
          </select>
          {!activ ? (
            <button type="button" data-testid="voice-porneste" onClick={porneste} disabled={ocupat || !config}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-brand/50 text-brand hover:bg-brand/10 disabled:opacity-50">
              {ocupat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
              Pornește
            </button>
          ) : (
            <button type="button" data-testid="voice-opreste" onClick={opreste}
                    className="flex items-center gap-2 px-3 py-2 text-sm border border-line/20 hover:border-red-400/50 hover:text-red-400">
              <MicOff className="w-4 h-4" /> Oprește
            </button>
          )}
        </div>

        {profilAles && (
          <p className="text-xs text-fg/50 leading-relaxed" data-testid="voice-nota-profil">
            {profilAles.nota}{" "}
            {profilAles.efectiv?.cere_trezire_mereu
              ? `Spune „${config.cuvant_trezire}" înainte de fiecare replică.`
              : `Spune „${config.cuvant_trezire}" o dată; pe urmă vorbește normal.`}
          </p>
        )}

        {istoric.length > 0 && (
          <ul className="space-y-2 max-h-72 overflow-y-auto" data-testid="voice-istoric">
            {istoric.map((r, i) => (
              <li key={i} className="text-sm">
                {r.cine === "eu" ? (
                  <span className={r.pentru_mine === false ? "text-fg/30" : "text-fg/70"}>
                    <span className="label-tiny mr-2">eu</span>{r.text}
                    {r.pentru_mine === false && (
                      <span className="text-fg/30 text-xs"> — {r.nota}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-fg">
                    <span className="label-tiny mr-2 text-brand">atena</span>{r.rostit}
                    {r.mai_e_scris && (
                      <span className="block mt-1 text-xs text-fg/50">{r.text}</span>
                    )}
                    {r.unealta && (
                      <span className="ml-2 text-[11px] mono text-fg/30">{r.unealta}</span>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
